import { create } from "zustand";
import type { User } from "@ebook/contracts";
import { api } from "../lib/api-client";
import { disableGoogleAutoSelect } from "../lib/google-identity";
import { useLocaleStore } from "./locale-store";

type AuthState = {
  user: User | null;
  loading: boolean;
  error: string | null;
  restore: () => Promise<void>;
  completeGoogleSignIn: (credential: string) => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User) => void;
};

async function syncSettingsLocale() {
  try {
    const settings = await api.settings();
    useLocaleStore.getState().setLocale(settings.locale);
  } catch {
    useLocaleStore.getState().resetLocale();
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  error: null,
  restore: async () => {
    try {
      const user = await api.me();
      await syncSettingsLocale();
      set({ user, loading: false });
    } catch {
      useLocaleStore.getState().resetLocale();
      set({ user: null, loading: false });
    }
  },
  completeGoogleSignIn: async (credential) => {
    set({ loading: true, error: null });
    try {
      const user = await api.createSession(credential);
      await syncSettingsLocale();
      set({ user, loading: false });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Google sign in failed", loading: false });
      throw error;
    }
  },
  signOut: async () => {
    await api.logout().catch(() => undefined);
    disableGoogleAutoSelect();
    useLocaleStore.getState().resetLocale();
    set({ user: null });
  },
  setUser: (user) => set({ user }),
}));
